import { useCallback, useContext, useEffect, useState } from "react";
import { getVendorByTenantId } from "manifest-core/classes/b2c/vendorConfig";
import { UserContext } from "../context/User/userProvider";
import Session from "../utils/session";
import { RoutePaths, useTypedNavigate } from "../routes/RoutePaths";
import { AppContext } from "../context/App/appProvider";
import { isDevelopmentEnvironment, isStagingEnvironment } from "../env";

/**
 * This hook makes fetching user data much simpler.
 */

export default function useUser() {
  const navigate = useTypedNavigate();
  const { setShowFullScreenLoader } = useContext(AppContext);
  const { user, getUser, userFetched, fetchingUser } = useContext(UserContext);
  const [firstTimeLoading, setFirstTimeLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [customUrl, setCustomUrl] = useState<string | undefined>(undefined);

  const init = useCallback(() => {
    if (fetchingUser) {
      setLoading(true);
      return;
    }
    if (!fetchingUser && userFetched) {
      if (!user) {
        Session.clearSession();
        navigate(RoutePaths.error404);
        return;
      }
      setLoading(false);
      setShowFullScreenLoader(false);
      setFirstTimeLoading(false);
      return;
    }
    if (!fetchingUser) {
      getUser();
    }
  }, [fetchingUser, userFetched]);

  useEffect(() => {
    if (!user) return;
    // FOR TESTING PURPOSE
    if (isDevelopmentEnvironment() && user.tenantId === 1951) {
      setCustomUrl("dev-testing");
    } else if (isStagingEnvironment() && user.tenantId === 1701) {
      setCustomUrl("staging-testing");
    } else {
      const vendor = getVendorByTenantId(user.tenantId);
      if (vendor) {
        setCustomUrl(vendor.id);
      }
    }
  }, [user]);

  useEffect(() => {
    setShowFullScreenLoader(true);
  }, []);

  useEffect(init, [init]);

  return {
    firstTimeLoading,
    getUser,
    loading,
    user,
    customUrl,
  };
}
